import Styles from 'src/components/Styles';
import {
  StyleSheet,
  View,
  Image,
  Platform,
  PixelRatio,
  Text,
  FlatList,
} from 'react-native';
import {
  PickupLocationStylesDescriptor,
  PickupTimeStylesDescriptor,
  ProductDetailsStylesDescriptor,
  MenuListStylesDescriptor,
  FlashListPropsType,
  Identifiable,
  ProductComponentProps,
  AddItemButtonProps,
  UpsellButtonProps,
  DepositStylesDescriptor,
} from 'src/components/screens/orderAhead/descriptor/DescriptorType';
import Localized from 'src/constants/AppStrings';
import {ensureHttps} from 'src/services/StringUtils';
import AVText from 'src/components/elements/AVText';
import {MenuProductType} from 'src/types/Menu';
import {DNA} from 'src/types/dnaTypes';
import {TransactionDetail} from 'src/types/TransactionDetail';
import {ListScanItemsProps} from 'src/components/screens/scanAndPay/listScanItems/ListScanItems';
import {
  DietaryHealthGood,
  DietaryHealthWarning,
  DietaryHealthAlert,
} from 'src/components/img/svg/DietaryHealthGrades';
import AVFormattedCurrency from 'src/components/elements/AVFormattedCurrency';
import TagLightIcon from 'src/components/img/svg/TagLightIcon';
import AccountStore from 'src/stores/AccountStore';
import RecentComboIcon from 'src/components/img/svg/RecentComboIcon';
import MenuItemListTemplate2 from 'src/components/elements/orderAhead/MenuListItemTemplate2';
import DashedLine from 'react-native-dashed-line';
import {BarButton} from 'src/components/elements/orderAhead/BarButton';
import TransactionUpsellItem from 'src/components/elements/orderAhead/TransactionalUpsell';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';
import Util from 'src/Util';

const {
  bodyFontSize,
  subHeaderFontSize,
  bodySmallFontSize,
  bodyLargeFontSize,
  headerFontSize,
  mainFontFamily,
} = getLayoutDescriber();

export function getPickupLocationStyles(): PickupLocationStylesDescriptor {
  return StyleSheet.create({
    buttonText: {
      color: Styles.Colors.CompanyKitchen.black,
      fontFamily: mainFontFamily(),
      fontSize: bodyFontSize(),
      fontWeight: '700',
    },
    buttonContainerStyle: {
      marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
      marginBottom: Styles.Spacing.m3 + Styles.Spacing.m2,
      marginTop: Styles.Spacing.m3,
      alignSelf: 'center',
      width: Platform.OS === 'web' ? '100%' : null,
    },
    errorText: {
      alignItems: 'center',
      fontWeight: '700',
      fontFamily: mainFontFamily(),
      fontSize: subHeaderFontSize(),
      color: Styles.Colors.PayNew.error,
      paddingHorizontal: Styles.Spacing.m1,
      marginBottom: 1,
    },
    headerText: {
      color: Styles.darkColor,
      fontWeight: '700',
      fontFamily: mainFontFamily(),
      marginBottom: Styles.Spacing.m1,
      fontSize: subHeaderFontSize(),
    },
    errorContainer: {
      paddingTop: Styles.Spacing.m1,
      flexDirection: 'row',
      alignItems: 'center',
    },
    containerStyle: {
      width: Platform.OS === 'web' ? '100%' : Styles.ScreenWidth,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    },
    buttonView: {
      width: '100%',
    },
    upsellPointsView: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'baseline',
      paddingTop: 5,
    },
    upsellPointsText: {
      flex: 1,
      fontSize: bodySmallFontSize(),
      fontWeight: '400',
      color: Styles.Colors.PayNew.darkHuesBase03,
      fontFamily: mainFontFamily(),
    },
  });
}

export function renderColorNutritionComponent(
  nutritionDisplayColor: string,
  nutritionDisplayLetter: string,
  nutritionCalorieTotal: number,
) {
  let healthGradeIcon: JSX.Element | false;
  if (
    nutritionDisplayLetter?.toUpperCase() === 'G' &&
    nutritionDisplayColor?.toUpperCase() === 'GREEN'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: good, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: good, "
      >
        <DietaryHealthGood />
      </View>
    );
  } else if (
    nutritionDisplayLetter?.toUpperCase() === 'Y' &&
    nutritionDisplayColor?.toUpperCase() === 'YELLOW'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: warning, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: warning, "
      >
        <DietaryHealthWarning />
      </View>
    );
  } else if (
    nutritionDisplayLetter?.toUpperCase() === 'R' &&
    nutritionDisplayColor?.toUpperCase() === 'RED'
  ) {
    healthGradeIcon = (
      <View
        accessible={true}
        accessibilityLabel="Health grade: alert, "
        accessibilityRole="image"
        role="img"
        aria-label="Health grade: alert, "
      >
        <DietaryHealthAlert />
      </View>
    );
  }

  return (
    <View>
      {nutritionCalorieTotal > 0 && (
        <View>
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              marginLeft: -Styles.Spacing.m1,
              marginTop: Styles.Spacing.m3,
              marginBottom: Styles.Spacing.m2,
            }}
          >
            <View style={{padding: Styles.Spacing.m1}}>{healthGradeIcon}</View>
            <View
              style={{
                alignItems: 'flex-start',
              }}
            >
              <AVText
                testID={'qa_CaloriesValue_txt'}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
                style={{
                  marginBottom: Styles.Spacing.m1,
                  color: '#22212D',
                  fontSize: bodyLargeFontSize(),
                  fontFamily: mainFontFamily(),
                  fontWeight: 'bold',
                  alignItems: 'flex-start',
                  paddingLeft: Styles.Spacing.m1,
                }}
              >
                {nutritionCalorieTotal}
              </AVText>
            </View>
            <View style={Styles.Style.labelColumn2}>
              <AVText
                testID={'qa_TotalCalories_txt'}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
                style={{
                  marginBottom: Styles.Spacing.m1,
                  color: '#22212D',
                  fontSize: bodyLargeFontSize(),
                  fontFamily: mainFontFamily(),
                  fontWeight: 'bold',
                  alignItems: 'flex-start',
                  paddingLeft: Styles.Spacing.m1,
                }}
              >
                {this.props?.location?.locationFeatures?.dna
                  ?.showCaloriesAsKilocalories
                  ? Localized.Labels.total_kilocalories
                  : Localized.Labels.total_calories}
              </AVText>
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

export function contactPermissionErrorText() {
  return Localized.Labels.formatString(
    Localized.Labels.choose_contact_permission_error,
    Localized.Labels.app_name_company_kitchen,
  );
}

export function renderNutritionPromotionComponent(
  nutritionPromoName: string,
  nutritionDiscountAmount: number,
) {
  return (
    !isNaN(nutritionDiscountAmount) &&
    nutritionPromoName !== undefined &&
    nutritionPromoName !== '' && (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          marginTop: Styles.Spacing.m1,
          marginBottom: Styles.Spacing.m1,
        }}
      >
        <View>
          <View
            testID={'qa_NutritionPromoLabel_txt'}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginRight: Styles.Spacing.m3,
            }}
          >
            <TagLightIcon
              fill={Styles.Colors.Canteen.green}
              style={{marginRight: Styles.Spacing.m2}}
            />
            <AVText
              testID={'qa_NutritionPromoLabel_txt'}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
              style={{
                color: Styles.Colors.Canteen.green,
                fontFamily: mainFontFamily(),
                fontSize: bodyLargeFontSize(),
              }}
            >
              {nutritionPromoName}
            </AVText>
          </View>
        </View>
        <View testID={'qa_NutritionPromoValue_txt'}>
          <AVFormattedCurrency
            currency={AccountStore.getCurrency()}
            value={nutritionDiscountAmount * -1}
            style={{
              color: Styles.Colors.Canteen.green,
              fontFamily: mainFontFamily(),
              fontSize: bodyLargeFontSize(),
            }}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
            accessibilityLabel={`${Localized.Labels.discounts}, $${(
              nutritionDiscountAmount * -1
            ).toString()}`}
            accessibilityRole="text"
            aria-label={`${Localized.Labels.discounts}, $${(
              nutritionDiscountAmount * -1
            ).toString()}, text`}
          />
        </View>
      </View>
    )
  );
}

export function getInactiveButtonColor() {
  return Styles.Colors.PayNew.darkHuesBase04;
}

export function textColor() {
  return Styles.darkColor;
}

export function calorieTextColor() {
  return Styles.Colors.PayNew.darkHuesBase03;
}

export function getPickupTimeStyles(): PickupTimeStylesDescriptor {
  return StyleSheet.create({
    dateView: {
      marginLeft: Styles.Spacing.m3,
      marginTop: Styles.Spacing.m4 - Styles.Spacing.m2,
    },
    timeView: {
      marginTop: Styles.Spacing.m4 - Styles.Spacing.m2,
    },
    pickupTimeContainer: {
      flex: 1,
      marginTop: Styles.Heights.h2,
    },
    pickupLocationHeaderView: {
      justifyContent: 'space-between',
      alignItems: 'center',
      flexDirection: 'row',
      minHeight: Styles.Heights.touchTargetHeight2,
      width: '100%',
      marginLeft: Styles.Spacing.m3,
      paddingRight: Styles.Heights.h1,
    },
    tilesStyle: {
      marginRight: Styles.Spacing.m3 - Styles.Spacing.m1,
      marginTop: Styles.Spacing.m3,
    },
    customTileButtonTime: {
      minWidth: 105,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: Styles.Spacing.m1,
    },
    errorContainer: {
      flexDirection: 'row',
      marginTop: Styles.Spacing.m2,
      alignItems: 'center',
    },
    editButton: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: Styles.Colors.CompanyKitchen.black,
      borderRadius: 32,
      paddingHorizontal: 8,
      paddingVertical: 5,
      marginRight: Styles.Spacing.m3,
      overflow: 'hidden',
    },
    buttonTextStyle: {
      fontWeight: '700',
      paddingHorizontal: Styles.Spacing.m1,
      fontSize: bodyFontSize(),
      color: Styles.Colors.CompanyKitchen.black,
      marginBottom: 1,
      fontFamily: mainFontFamily(),
    },
    dateText: {
      fontWeight: '700',
      fontSize: bodyLargeFontSize(),
      color: Styles.darkColor,
      fontFamily: mainFontFamily(),
    },
  });
}
export function timeErrorMessage() {
  return Localized.Errors.please_select_time;
}
export function getActiveButtonColor() {
  return Styles.Colors.CompanyKitchen.green;
}
export function productDetailsTitle() {
  return Localized.Labels.item_info;
}
export function locationErrorMessage() {
  return Localized.Errors.please_select_location;
}
export function dayErrorMessage() {
  return Localized.Errors.please_select_day;
}

export function productDetailsImageView(imageUri: string): JSX.Element {
  return (
    <>
      <View>
        {imageUri ? (
          <Image
            accessible={true}
            accessibilityLabel="Product image"
            accessibilityRole="image"
            aria-label="Product image"
            role="img"
            style={{aspectRatio: 16 / 9}}
            source={{uri: ensureHttps(imageUri)}}
            resizeMode={'cover'}
          />
        ) : (
          <Image
            accessible={true}
            accessibilityLabel="Default Product image"
            accessibilityRole="image"
            aria-label="Default Product image"
            role="img"
            source={require('src/components/img/CKMenuItem.png')}
            resizeMode={'cover'}
            style={{width: '100%'}}
          />
        )}
      </View>
    </>
  );
}

export function productDetailsStyles(): ProductDetailsStylesDescriptor {
  return StyleSheet.create({
    content: {
      marginVertical: Styles.Spacing.m1,
    },
    container: {
      flex: 1,
    },
    infoContainer: {
      paddingVertical: Styles.Spacing.m1,
      paddingHorizontal: Styles.Spacing.m3,
      flexDirection: 'column',
    },
    title: {
      fontSize: headerFontSize(),
      fontWeight: '700',
      marginBottom: Styles.Spacing.m2,
      color: Styles.Colors.PayNew.black01,
      fontFamily: mainFontFamily(),
    },
    productPrice: {
      marginTop: 1,
      fontSize: headerFontSize(),
      fontWeight: '700',
      marginBottom: Styles.Spacing.m2,
      color: Styles.Colors.PayNew.black01,
      fontFamily: mainFontFamily(),
    },
    calories: {
      fontSize: headerFontSize(),
      fontWeight: '700',
      color: Styles.Colors.PayNew.black01,
      fontFamily: mainFontFamily(),
      marginTop: 1,
    },
    calorieText: {
      fontSize: bodySmallFontSize(),
      fontWeight: '400',
      color: Styles.Colors.PayNew.black01,
      fontFamily: Styles.FontFamily.latoRegular,
      marginLeft: Styles.Spacing.m1,
      marginTop: Styles.Spacing.m2 + 2,
    },
    verticalLine: {
      marginHorizontal: Styles.Spacing.m2,
      backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
      borderColor: Styles.Colors.PayNew.darkHuesBase04,
      borderWidth: 0.8,
    },
    healthGrade: {
      marginLeft: Styles.Spacing.m2,
      alignItems: 'center',
      justifyContent: 'center',
    },

    nutritionButtonView: {
      flexDirection: 'row',
      marginVertical: Styles.Spacing.m2,
    },
    outlined: {
      flexDirection: 'row',
      justifyContent: 'center',
      borderRadius: Styles.Spacing.m4,
      borderColor: Styles.Colors.CompanyKitchen.green,
      marginTop: Styles.Spacing.m2,
      paddingVertical: Styles.Spacing.m2,
      paddingHorizontal: Styles.Spacing.m3,
      borderWidth: 2,
      overflow: 'hidden',
      backgroundColor: Styles.white,
    },
    outlinedButtonText: {
      color: Styles.Colors.CompanyKitchen.black,
      fontSize: bodyFontSize(),
      fontWeight: '700',
      fontFamily: mainFontFamily(),
    },
    descriptionContainer: {
      marginHorizontal: Styles.Spacing.m3,
    },
    description: {
      fontSize: bodySmallFontSize(),
      color: Styles.Colors.PayNew.darkHuesBase04,
      fontFamily: mainFontFamily(),
      marginBottom: Styles.Spacing.m1,
    },
    customizationSection: {
      flexDirection: 'column',
      flex: 1,
    },
    quantityContainer: {
      marginTop: Styles.Spacing.m0,
    },
    quantitySelectionView: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginHorizontal: Styles.Spacing.m3,
    },
    dashStyle: {
      borderRadius: 100,
      marginVertical: Styles.Spacing.m3,
      height: PixelRatio.roundToNearestPixel(2),
      overflow: 'hidden',
      width: '100%',
    },
    quantityLabel: {
      fontSize: subHeaderFontSize(),
      color: Styles.Colors.PayNew.black01,
      fontWeight: '400',
      fontFamily: mainFontFamily(),
      marginRight: Styles.Spacing.m3 + Styles.Spacing.m2,
      minHeight: 48,
      marginTop: 10,
    },
  });
}

export function roundedButtonColor() {
  return Styles.Colors.CompanyKitchen.green;
}
export function dashLineColor() {
  return '#999';
}

export function menuListStyles(): MenuListStylesDescriptor {
  return StyleSheet.create({
    content: {
      flex: 1,
    },
    searchContainer: {
      marginLeft: Styles.Spacing.m3,
      marginRight: Styles.Spacing.m3,
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: Styles.Spacing.m3,
      marginTop: Styles.Spacing.m3,
    },
    categories: {
      marginTop: Styles.Spacing.m1,
      paddingBottom: Styles.Spacing.m2 + 2,
    },
  });
}
export function getFlashListProps<T extends Identifiable>({
  data,
  renderItem,
  refreshKey,
}: FlashListPropsType<T>) {
  return {
    data,
    renderItem,
    keyExtractor: (item: T) => item.id,
    ListEmptyComponent: (
      <View style={styles.listEmptyStyles}>
        <AVText
          style={{
            fontSize: bodyLargeFontSize(),
            fontFamily: mainFontFamily(),
            fontWeight: '700',
            color: Styles.Colors.PayNew.black01,
          }}
        >
          {Localized.Labels.no_pdt_found}
        </AVText>
        <View style={{alignSelf: 'center'}}>
          <RecentComboIcon
            fillColor={Styles.Colors.Canteen.primary}
            secondaryColor={Styles.Colors.Canteen.lightGreen}
          />
        </View>
      </View>
    ),
    estimatedItemSize: 72,
    extraData: refreshKey,
    ListFooterComponent: <View style={{height: Styles.Heights.h4}} />,
  };
}

export function getProductComponent(props: ProductComponentProps) {
  return (
    <MenuItemListTemplate2
      dnaConfiguration={props?.dnaConfiguration}
      onPress={props?.onPress}
      name={props?.name}
      item={props?.item}
      price={props?.price}
      imageUrl={props?.imageUrl}
      isProductCat={props?.isProductCat}
      index={props?.index}
      key={props?.index}
      overrideDna={props?.overrideDna}
    />
  );
}

export function getTransactionUpsellName(_name: string) {
  return (
    <Text
      style={styles.upsellName}
      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
      aria-label={`${_name}, text`}
      accessibilityLabel-label={`${_name}, text`}
      accessible
      testID="qa_Checkout.UpsellTitle_txt"
      nativeID="qa_Checkout.UpsellTitle_txt"
    >
      {_name}
    </Text>
  );
}

export function getTransactionUpsellProductsList(
  products: MenuProductType[],
  addItem: (item: MenuProductType) => void,
  dna?: DNA,
) {
  return (
    <View style={styles.upsellList}>
      <FlatList
        horizontal
        data={products}
        keyExtractor={(item) => item.id}
        style={styles.flatListStyle}
        contentContainerStyle={styles.containerStyle}
        renderItem={({item}) => (
          <TransactionUpsellItem
            item={item}
            onPress={() => addItem(item)}
            dna={dna}
          />
        )}
      />
    </View>
  );
}

export function getEditLocationIconColor() {
  return Styles.Colors.CompanyKitchen.black;
}
export function getEmptyCartlblStyle() {
  return {
    fontFamily: mainFontFamily(),
    fontSize: bodyFontSize(),
    fontWeight: '400',
    textAlign: 'left',
    color: Styles.Colors.PayNew.black01,
  };
}
export function activeCalendarIconColor() {
  return Styles.Colors.CompanyKitchen.black;
}

export function inActiveCalendarIconColor() {
  return Styles.Colors.CompanyKitchen.black;
}

export function renderTenderDiscount(
  _transaction: TransactionDetail | null,
  _props: ListScanItemsProps,
) {
  return null;
}

export function depositStyles(): DepositStylesDescriptor {
  return StyleSheet.create({
    depositView: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: Styles.Spacing.m2,
    },
    transactionItemsDeposit: {
      color: Styles.Colors.PayNew.black01,
      fontFamily: mainFontFamily(),
      fontSize: bodyFontSize(),
    },
  });
}

export function getAndroidImageHeight() {
  return Styles.ScreenHeight < 700 ? 470 : 445;
}

export function getDashedLines() {
  return (
    <DashedLine
      dashThickness={PixelRatio.roundToNearestPixel(1)}
      dashGap={PixelRatio.roundToNearestPixel(4)}
      dashLength={PixelRatio.roundToNearestPixel(3)}
      dashColor={dashLineColor()}
      style={styles.dashStyle}
    />
  );
}
export function showTotalAmount(total, props) {
  return (
    <View style={styles.transactionTotalView}>
      <AVText
        maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
        accessible={true}
        accessibilityRole="text"
        accessibilityLabel={Localized.Labels.total}
        aria-label={Localized.Labels.total}
        style={styles.transactionTotal}
        testID="qa_Total_txt"
        nativeID="qa_Total_txt"
      >
        {Localized.Buttons.your_total}
      </AVText>
      <AVFormattedCurrency
        style={styles.transactionTotal}
        value={total}
        accessibilityLabel={`${
          Localized.Buttons.your_total
        } ${Util.formatCurrency(props, total, AccountStore.getCurrency())}`}
        currency={AccountStore.getCurrency()}
        maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
        accessible
        testID="qa_TotalValue_txt"
        nativeID="qa_TotalValue_txt"
      />
    </View>
  );
}

export function getAddItemButton(props: AddItemButtonProps) {
  return (
    <View style={styles.addItembtnView}>
      <BarButton
        disabled={props.isDiabled}
        leftText={props.label}
        onPress={props.onPress}
        buttonViewStyle={[
          styles.addBtnStyle,
          {
            backgroundColor: props.isDiabled
              ? Styles.Colors.PayNew.darkHuesBase04
              : Styles.Colors.CompanyKitchen.green,
          },
        ]}
        containerStyle={styles.addBtnContainer}
        rightText={props.rightText}
        textStyle={[
          styles.addBtnText,
          {
            color: props.isDiabled
              ? Styles.Colors.PayNew.white01
              : Styles.Colors.PayNew.black01,
          },
        ]}
        testID="qa_AddItem_btn"
        rightTestID="qa_AddItemAmount_txt"
      />
    </View>
  );
}

export function upsellSelectedBackground() {
  return 'rgba(58, 150, 219, 0.16)';
}

export function upsellSheetStyles() {
  return StyleSheet.create({
    upsellSelectedView: {
      borderWidth: 2,
      borderColor: Styles.Colors.CompanyKitchen.primary,
    },
  });
}

export function getUpsellButton(props: UpsellButtonProps) {
  return (
    <View style={styles.addItembtnView}>
      <BarButton
        disabled={props.isDiabled}
        leftText={props.label}
        onPress={props.onPress}
        buttonViewStyle={[
          styles.addBtnStyle,
          {
            backgroundColor: props.isDiabled
              ? Styles.Colors.PayNew.darkHuesBase04
              : Styles.Colors.CompanyKitchen.green,
            justifyContent: props.isDiabled ? 'center' : 'space-between',
          },
        ]}
        containerStyle={styles.addBtnContainer}
        rightText={!props.isDiabled && props.rightText}
        textStyle={[
          styles.addBtnText,
          {
            color: props.isDiabled
              ? Styles.Colors.PayNew.white01
              : Styles.Colors.PayNew.black01,
          },
        ]}
        rightTestID="qa_UpsellAmount_txt"
        testID="qa_UpdateItem_btn"
      />
    </View>
  );
}

export function getiOSImageHeight() {
  return Styles.ScreenHeight < 700 ? 400 : 440;
}

const styles = StyleSheet.create({
  dashStyle: {
    borderRadius: 100,
    height: PixelRatio.roundToNearestPixel(2),
    marginVertical: Styles.Spacing.m2,
    overflow: 'hidden',
    marginHorizontal: Styles.Spacing.m3,
  },
  transactionTotalView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m3,
  },
  transactionTotal: {
    fontWeight: '700',
    color: Styles.Colors.PayNew.black01,
    fontFamily: mainFontFamily(),
    fontSize: subHeaderFontSize(),
  },
  addItembtnView: {
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginTop: Styles.Spacing.m3,
    alignSelf: 'center',
  },
  addBtnStyle: {
    width: Platform.OS === 'web' ? '50%' : '100%',
    backgroundColor: Styles.Colors.CompanyKitchen.green,
    justifyContent: 'space-between',
    height: Styles.Heights.touchTargetHeight1 + 2,
  },
  addBtnContainer: {
    width: Styles.ScreenWidth,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  addBtnText: {
    fontFamily: mainFontFamily(),
    fontSize: bodyFontSize(),
    fontWeight: '700',
    color: Styles.Colors.PayNew.black01,
  },
  upsellName: {
    fontFamily: mainFontFamily(),
    fontSize: bodyFontSize(),
    color: Styles.Colors.PayNew.black01,
    fontWeight: '700',
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  upsellList: {
    flex: 1,
    marginVertical: Styles.Spacing.m2,
  },
  flatListStyle: {
    width: '100%',
  },
  containerStyle: {paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2},
  listEmptyStyles: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: Styles.Spacing.m3,
    marginTop:
      Platform.OS === 'web' ? Styles.ScreenHeight / 4 : Styles.ScreenWidth / 3,
  },
});

const announcementPopUpButtonColor = '#84BD00';

export default {
  textColor,
  getPickupTimeStyles,
  productDetailsImageView,
  productDetailsStyles,
  roundedButtonColor,
  dashLineColor,
  menuListStyles,
  getPickupLocationStyles,
  getInactiveButtonColor,
  getFlashListProps,
  getActiveButtonColor,
  locationErrorMessage,
  dayErrorMessage,
  timeErrorMessage,
  productDetailsTitle,
  getProductComponent,
  getEmptyCartlblStyle,
  getTransactionUpsellName,
  getTransactionUpsellProductsList,
  getEditLocationIconColor,
  activeCalendarIconColor,
  inActiveCalendarIconColor,
  renderTenderDiscount,
  renderColorNutritionComponent,
  renderNutritionPromotionComponent,
  getAndroidImageHeight,
  calorieTextColor,
  getDashedLines,
  showTotalAmount,
  getAddItemButton,
  upsellSelectedBackground,
  upsellSheetStyles,
  getUpsellButton,
  contactPermissionErrorText,
  getiOSImageHeight,
  announcementPopUpButtonColor,
  depositStyles,
};
